import { useState, useRef, useEffect, useMemo } from 'react';

import { useQuery } from "@apollo/client/react";

import { GET_LINE_RICH_MENU_SELECTOR_DIALOG_LIST } from '../../../graphql';

const LIMIT = 20;

export const useGetRichMenus = (storeId: string) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<() => void>(undefined);

  const { data, loading, fetchMore } = useQuery(GET_LINE_RICH_MENU_SELECTOR_DIALOG_LIST, {
    variables: {
      input: {
        storeId,
        offset: 0,
        limit: LIMIT,
      },
    },
  });

  const numOfRichMenu = data?.LineRichMenus?.records.length ?? 0;
  const total = data?.LineRichMenus?.total ?? 0;

  // Rich Menus for Selector List
  const richMenuList = useMemo(() => {
    if (!data?.LineRichMenus?.records) return [];
    return data.LineRichMenus.records.map(({ id, title, imageUrl }) => {
      return {
        id,
        title,
        imageUrl: imageUrl ?? undefined,
      };
    });
  }, [data?.LineRichMenus?.records]);

  // Fetch more function for infinite scroll
  useEffect(() => {
    loadMoreRef.current = async () => {
      // required for first load will always start in 0
      if (numOfRichMenu > 0 && total > 0 && numOfRichMenu >= total) return;

      try {
        setIsLoadingMore(true);
        await fetchMore({
          variables: {
            input: {
              storeId,
              limit: LIMIT,
              offset: numOfRichMenu === 0 ? 20 : numOfRichMenu,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            const prevResult = prev.LineRichMenus?.records || [];
            const newResult = fetchMoreResult.LineRichMenus?.records || [];
            const total = prev.LineRichMenus?.total ?? 0;

            return {
              LineRichMenus: {
                ...prev.LineRichMenus,
                total,
                records: [...prevResult, ...newResult],
              },
            };
          },
        });
      } catch (error) {
        // Do nothing
      } finally {
        setIsLoadingMore(false);
      }
    };
  }, [fetchMore, storeId, numOfRichMenu, total]);

  return {
    richMenuList,
    loading,
    fetchMoreRichMenus: loadMoreRef.current,
    isLoadingMore,
  };
};
